import React, { useState } from "react";
import {
  Cloud,
  Globe,
  Plus,
  Rocket,
  LayoutGrid,
  Facebook,
  Linkedin,
} from "lucide-react";
import panda from "../../assets/images/red-panda.png";
import ResponsiveImage from "../common/ResponsiveImage";
import ContactCard from "../common/Card/ContactCard";

// aria-hidden — ContactCard (or any wrapper) provides the accessible label via
// surrounding text. Avoids the axe rule "<svg role='img'> must have alt text".
const ZaloIcon = ({ width = 20, height = 20, className = "" }) => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    width={width}
    height={height}
    fill="currentColor"
    className={className}
  >
    <path d="M12.49 10.2722v-.4496h1.3467v6.3218h-.7704a.576.576 0 01-.5763-.5729l-.0006.0005a3.273 3.273 0 01-1.9372.6321c-1.8138 0-3.2844-1.4697-3.2844-3.2823 0-1.8125 1.4706-3.2822 3.2844-3.2822a3.273 3.273 0 011.9372.6321l.0006.0005zM6.9188 7.7896v.205c0 .3823-.051.6944-.2995 1.0605l-.03.0343c-.0542.0615-.1815.206-.2421.2843L2.024 14.8h4.8948v.7682a.5764.5764 0 01-.5767.5761H0v-.3622c0-.4436.1102-.6414.2495-.8476L4.8582 9.23H.1922V7.7896h6.7266zm8.5513 8.3548a.4805.4805 0 01-.4803-.4798v-7.875h1.4416v8.3548H15.47zM20.6934 9.6C22.52 9.6 24 11.0807 24 12.9044c0 1.8252-1.4801 3.306-3.3066 3.306-1.8264 0-3.3066-1.4808-3.3066-3.306 0-1.8237 1.4802-3.3044 3.3066-3.3044zm-10.1412 5.253c1.0675 0 1.9324-.8645 1.9324-1.9312 0-1.065-.865-1.9295-1.9324-1.9295s-1.9324.8644-1.9324 1.9295c0 1.0667.865 1.9312 1.9324 1.9312zm10.1412-.0033c1.0737 0 1.945-.8707 1.945-1.9453 0-1.073-.8713-1.9436-1.945-1.9436-1.0753 0-1.945.8706-1.945 1.9436 0 1.0746.8697 1.9453 1.945 1.9453z" />
  </svg>
);

type Props = {
  pandaSrc?: string;
};

type ConsultationFormValues = {
  name: string;
  email: string;
  phone: string;
  service: string;
  details?: string;
};

type FieldErrors = Partial<Record<keyof ConsultationFormValues, string>>;

const serviceOptions = [
  { value: "seo", label: "Dịch vụ SEO" },
  { value: "website-design", label: "Dịch vụ thiết kế Website" },
  { value: "mobile-app", label: "Dịch vụ làm App Mobile" },
  { value: "zalo-mini-app", label: "Dịch vụ thiết kế Zalo Mini App" },
  { value: "content-writing", label: "Dịch vụ viết Content" },
  { value: "security", label: "Dịch vụ Bảo Mật" },
  { value: "larksuite-consulting", label: "Dịch vụ tư vấn Larksuite" },
];

const contactEmail = "nickyhuynh@gmail.com";

const initialValues: ConsultationFormValues = {
  name: "",
  email: "",
  phone: "",
  service: "",
  details: "",
};

const inputClass =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-[var(--textBlue58)] focus:outline-none focus:ring-1 focus:ring-[var(--textBlue58)]";

const labelClass = "block text-sm font-medium text-gray-700 mb-1 text-left";

const SectionForm: React.FC<Props> = ({ pandaSrc = panda }) => {
  const [values, setValues] = useState<ConsultationFormValues>(initialValues);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [toast, setToast] = useState<string | null>(null);

  const validate = (v: ConsultationFormValues): FieldErrors => {
    const e: FieldErrors = {};
    if (!v.name.trim()) e.name = "Vui lòng nhập họ và tên.";
    if (!v.email.trim()) e.email = "Vui lòng nhập email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) e.email = "Email không hợp lệ.";
    if (!v.phone.trim()) e.phone = "Vui lòng nhập số điện thoại.";
    else if (!/^(0|\+84)[0-9]{9,10}$/.test(v.phone)) e.phone = "Số điện thoại không hợp lệ.";
    if (!v.service) e.service = "Vui lòng chọn gói dịch vụ.";
    return e;
  };

  const handleChange = <K extends keyof ConsultationFormValues>(field: K, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const e = validate(values);
    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }

    const selectedService =
      serviceOptions.find((option) => option.value === values.service)?.label || values.service;

    const subject = `Yêu cầu tư vấn giải pháp - ${values.name}`;
    const body = [
      "Thông tin khách hàng:",
      `Họ và tên: ${values.name}`,
      `Email: ${values.email}`,
      `Số điện thoại: ${values.phone}`,
      `Nhu cầu quan tâm: ${selectedService}`,
      "",
      "Mô tả chi tiết:",
      values.details?.trim() || "Khách hàng chưa bổ sung mô tả chi tiết.",
    ].join("\n");

    const mailtoUrl = `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;

    setToast("Đã tạo sẵn email tư vấn để bạn gửi tới TrioCore Solutions.");
    setValues(initialValues);
    setErrors({});
    window.setTimeout(() => setToast(null), 4000);
  };

  return (
    <div id="contact-section" className="mt-20 w-full overflow-hidden bg-[var(--bgBlue)]">
      {toast && (
        <div
          role="status"
          className="fixed right-4 top-4 z-50 rounded-md bg-green-600 px-4 py-3 text-sm font-medium text-white shadow-lg"
        >
          {toast}
        </div>
      )}
      <section className="relative mt-20 w-full container">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-32 bottom-0 h-72 w-72 rounded-full bg-cyan-300/20 blur-3xl" />
          <div className="absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-blue-400/20 blur-3xl" />

          <Cloud size={60} className="absolute left-10 top-2 text-cyan-300 opacity-40" />
          <Globe
            size={50}
            className="absolute bottom-24 left-1/2 top-1/2 text-blue-400 opacity-40"
          />
          <LayoutGrid
            size={40}
            className="absolute bottom-12 right-[660px] text-cyan-400 opacity-30"
          />
          <Plus size={36} className="absolute right-4 top-2 text-blue-500 opacity-30" />
        </div>

        <div className="px-10 pb-12 pt-10">
          <div className="flex flex-col items-center justify-center gap-8 lg:flex-row">
            <div className="w-auto space-y-5 mobile523:w-[500px] lg:w-1/2">
              <div className="color-second relative mt-4 flex items-center gap-2 text-left text-xl font-bold mobile523:text-3xl">
                <span>Liên Hệ Nhận Tư Vấn Giải Pháp Doanh Nghiệp</span>
                <Rocket
                  size={60}
                  className="absolute right-[calc(50%-180px)] top-8 mb-0.5 rotate-45 text-[var(--textBlue57)] opacity-50 mobile523:right-[calc(50%+102px)]"
                />
              </div>

              <p className="color-grey w-auto text-left text-[14px] leading-[30px] mobile523:w-[500px] mobile523:text-[16px]">
                Vui lòng để lại thông tin và mô tả ngắn về nhu cầu hiện tại của doanh nghiệp. Đội
                ngũ TrioCore Solutions sẽ liên hệ, tư vấn hướng đi phù hợp và đề xuất giải pháp rõ
                mục tiêu trong thời gian sớm nhất.
              </p>

              <p className="color-grey flex flex-col gap-2 text-left">
                <span>
                  Hotline: <span className="font-bold color-grey">0822041909</span>
                </span>
                <span>
                  Email: <span className="font-bold color-grey">info@triocoresolutions.com</span>
                </span>
                <span>
                  Website:{" "}
                  <span className="font-bold color-grey">www.triocoresolutions.com.vn</span>
                </span>
              </p>

              <ul className="mt-[55px] space-y-8 text-slate-600">
                <li className="flex items-center">
                  <ContactCard
                    title="Live chat trực tiếp"
                    description="Facebook liên hệ của TrioCore Solutions"
                    icon={<Facebook size={20} className="text-red-600" fill="currentColor" />}
                  />
                </li>
                <li className="flex items-center">
                  <ContactCard
                    title="Kết nối Zalo"
                    description="Zalo liên hệ của TrioCore Solutions"
                    icon={<ZaloIcon className="text-red-600" width={20} height={20} />}
                  />
                </li>
                <li className="flex items-center">
                  <ContactCard
                    title="Kết nối LinkedIn"
                    description="LinkedIn liên hệ của TrioCore Solutions"
                    icon={<Linkedin size={20} className="text-red-600" fill="currentColor" />}
                  />
                </li>
              </ul>

              <ResponsiveImage
                src={pandaSrc}
                alt="Panda"
                className="absolute -bottom-4 -left-[80px] z-20 w-[200px] mobile427:-left-[70px] mobile484:left-[calc(50%-325px)] mobile523:left-[calc(50%-345px)] mobile598:left-[calc(50%-375px)] lg:left-[calc(50%-90px)] xlAnt:left-[calc(50%-85px)]"
              />
            </div>

            <div className="relative order-1 mt-10 flex w-full justify-center lg:order-2 lg:mt-0 lg:w-1/2">
              <div className="relative z-10 w-[480px] rounded-xl border-[3px] border-[var(--textBlue58)] bg-white/80 px-6 pt-6 shadow-xl backdrop-blur supports-[backdrop-filter]:bg-white/60">
                <div className="mb-4 flex items-center justify-center gap-2">
                  <h3 className="color-second text-lg mobile523:text-2xl">
                    Liên Hệ Nhận Tư Vấn Giải Pháp Doanh Nghiệp
                  </h3>
                </div>

                <form onSubmit={handleSubmit} noValidate className="px-5 pb-6">
                  <div className="mb-4">
                    <label htmlFor="cf-name" className={labelClass}>
                      Họ và tên
                    </label>
                    <input
                      id="cf-name"
                      type="text"
                      value={values.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="Nguyễn Văn A"
                      className={inputClass}
                      aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? "cf-name-err" : undefined}
                    />
                    {errors.name && (
                      <p id="cf-name-err" className="mt-1 text-left text-xs text-red-600">
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label htmlFor="cf-email" className={labelClass}>
                      Email
                    </label>
                    <input
                      id="cf-email"
                      type="email"
                      value={values.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="email@company.com"
                      className={inputClass}
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? "cf-email-err" : undefined}
                    />
                    {errors.email && (
                      <p id="cf-email-err" className="mt-1 text-left text-xs text-red-600">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label htmlFor="cf-phone" className={labelClass}>
                      Số điện thoại
                    </label>
                    <input
                      id="cf-phone"
                      type="tel"
                      value={values.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder="0822 041 909"
                      className={inputClass}
                      aria-invalid={!!errors.phone}
                      aria-describedby={errors.phone ? "cf-phone-err" : undefined}
                    />
                    {errors.phone && (
                      <p id="cf-phone-err" className="mt-1 text-left text-xs text-red-600">
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label htmlFor="cf-service" className={labelClass}>
                      Gói dịch vụ
                    </label>
                    <select
                      id="cf-service"
                      value={values.service}
                      onChange={(e) => handleChange("service", e.target.value)}
                      className={inputClass}
                      aria-invalid={!!errors.service}
                      aria-describedby={errors.service ? "cf-service-err" : undefined}
                    >
                      <option value="">Chọn dịch vụ</option>
                      {serviceOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.service && (
                      <p id="cf-service-err" className="mt-1 text-left text-xs text-red-600">
                        {errors.service}
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label htmlFor="cf-details" className={labelClass}>
                      Yêu cầu cụ thể (Nếu có)
                    </label>
                    <textarea
                      id="cf-details"
                      rows={4}
                      value={values.details}
                      onChange={(e) => handleChange("details", e.target.value)}
                      placeholder="Mô tả vấn đề doanh nghiệp bạn đang gặp phải hoặc mục tiêu công nghệ/marketing bạn muốn đạt được."
                      className={inputClass}
                    />
                  </div>

                  <div className="mb-2">
                    <button type="submit" className="button-primary mt-3 w-auto">
                      Gửi yêu cầu tư vấn
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SectionForm;
